// frontend/src/features/equipment/EquipmentForm.tsx

import { useEffect, useMemo, useRef } from 'react';
import {
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Grid,
  Fieldset,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useEquipmentCategories } from '@/hooks/useEquipment';
import { IconTrash } from '@tabler/icons-react';
import {
  CATEGORY_LABELS,
  SUBCATEGORY_LABELS,
  type Equipment,
  type EquipmentCreate,
  type EquipmentCondition,
  type EquipmentStatus,
} from '@/types/equipment';
import { BrandModelInput } from './EquipmentSetupWizard/components/BrandModelInput';
import type { BrandModelValue } from './EquipmentSetupWizard/types';
import { reverseMapBrandModel, getBrandName, getModelName } from './EquipmentSetupWizard/utils/equipmentMapping';

interface EquipmentFormProps {
  equipment?: Equipment;
  onSubmit: (data: EquipmentCreate) => void;
  onCancel: () => void;
  onDelete?: (equipmentId: string) => void;
  isLoading?: boolean;
}

interface FormValues {
  name: string;
  category: string;
  subcategory: string;
  brandModel: BrandModelValue;
  serial_number: string;
  install_date: string | null;
  purchase_date: string | null;
  purchase_price: number | '';
  currency: string;
  vendor: string;
  condition: EquipmentCondition | '';
  status: EquipmentStatus;
  notes: string;
}

const CURRENCIES = [
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '\u20AC EUR' },
  { value: 'GBP', label: '\u00A3 GBP' },
  { value: 'AUD', label: '$ AUD' },
  { value: 'CAD', label: '$ CAD' },
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

export function EquipmentForm({
  equipment,
  onSubmit,
  onCancel,
  onDelete,
  isLoading = false,
}: EquipmentFormProps) {
  const { data: categoriesData } = useEquipmentCategories();
  const isEditMode = !!equipment;

  const form = useForm<FormValues>({
    initialValues: {
      name: equipment?.name ?? '',
      category: equipment?.category ?? '',
      subcategory: equipment?.subcategory ?? '',
      brandModel: reverseMapBrandModel(equipment?.brand, equipment?.model, equipment?.subcategory),
      serial_number: equipment?.serial_number ?? '',
      install_date: equipment?.install_date ?? null,
      purchase_date: equipment?.purchase_date ?? null,
      purchase_price: equipment?.purchase_price ?? '',
      currency: equipment?.currency ?? 'USD',
      vendor: equipment?.vendor ?? '',
      condition: equipment?.condition ?? '',
      status: equipment?.status ?? 'active',
      notes: equipment?.notes ?? '',
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Name is required'),
      category: (value) => (value ? null : 'Category is required'),
    },
  });

  // Reset subcategory when category changes
  useEffect(() => {
    const currentCategory = form.values.category;
    const currentSubcategory = form.values.subcategory;

    if (currentCategory && categoriesData?.categories) {
      const validSubcategories = categoriesData.categories[currentCategory] || [];
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        form.setFieldValue('subcategory', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.category, categoriesData?.categories]);

  // Track previous subcategory to detect user-initiated changes
  const prevSubcategoryRef = useRef(form.values.subcategory);
  useEffect(() => {
    const prev = prevSubcategoryRef.current;
    prevSubcategoryRef.current = form.values.subcategory;
    // Reset brand/model when subcategory changes (not on initial mount)
    if (prev && prev !== form.values.subcategory) {
      form.setFieldValue('brandModel', {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.subcategory]);

  // Build category options
  const categoryOptions = useMemo(() => {
    if (!categoriesData?.categories) return [];
    return Object.keys(categoriesData.categories).map((key) => ({
      value: key,
      label: CATEGORY_LABELS[key] || key,
    }));
  }, [categoriesData?.categories]);

  // Build subcategory options based on selected category
  const subcategoryOptions = useMemo(() => {
    if (!categoriesData?.categories || !form.values.category) return [];
    const subcategories = categoriesData.categories[form.values.category] || [];
    return subcategories.map((key: string) => ({
      value: key,
      label: SUBCATEGORY_LABELS[key] || key,
    }));
  }, [categoriesData?.categories, form.values.category]);

  const handleSubmit = (values: FormValues) => {
    const data: EquipmentCreate = {
      name: values.name.trim(),
      category: values.category,
      subcategory: values.subcategory || undefined,
      brand: getBrandName(values.brandModel) || undefined,
      model: getModelName(values.brandModel) || undefined,
      serial_number: values.serial_number || undefined,
      install_date: values.install_date || undefined,
      purchase_date: values.purchase_date || undefined,
      purchase_price: typeof values.purchase_price === 'number' ? values.purchase_price : undefined,
      currency: values.currency || undefined,
      vendor: values.vendor || undefined,
      condition: values.condition || undefined,
      status: values.status,
      notes: values.notes || undefined,
    };
    onSubmit(data);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Fieldset legend="Basic Information">
          <Stack gap="sm">
            <TextInput
              label="Name"
              placeholder="e.g., Return Pump, Main Light"
              required
              {...form.getInputProps('name')}
            />

            <Grid>
              <Grid.Col span={6}>
                <Select
                  label="Category"
                  placeholder="Select category"
                  required
                  data={categoryOptions}
                  {...form.getInputProps('category')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Subcategory"
                  placeholder="Select subcategory"
                  data={subcategoryOptions}
                  disabled={!form.values.category}
                  {...form.getInputProps('subcategory')}
                />
              </Grid.Col>
            </Grid>

            <BrandModelInput
              subcategoryId={form.values.subcategory}
              value={form.values.brandModel}
              onChange={(value) => form.setFieldValue('brandModel', value)}
              disabled={!form.values.subcategory}
            />

            <TextInput
              label="Serial Number"
              placeholder="e.g., ABC12345"
              {...form.getInputProps('serial_number')}
            />

            <Grid>
              <Grid.Col span={6}>
                <DateInput
                  label="Install Date"
                  placeholder="When installed"
                  value={form.values.install_date}
                  onChange={(value) => form.setFieldValue('install_date', value)}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label="Condition"
                  placeholder="Select"
                  data={CONDITIONS}
                  {...form.getInputProps('condition')}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label="Status"
                  data={STATUSES}
                  {...form.getInputProps('status')}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Fieldset>

        <Fieldset legend="Purchase Information">
          <Stack gap="sm">
            <Grid>
              <Grid.Col span={6}>
                <DateInput
                  label="Purchase Date"
                  placeholder="When purchased"
                  value={form.values.purchase_date}
                  onChange={(value) => form.setFieldValue('purchase_date', value)}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <NumberInput
                  label="Purchase Price"
                  placeholder="0.00"
                  decimalScale={2}
                  min={0}
                  {...form.getInputProps('purchase_price')}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label="Currency"
                  data={CURRENCIES}
                  {...form.getInputProps('currency')}
                />
              </Grid.Col>
            </Grid>

            <TextInput
              label="Vendor"
              placeholder="e.g., Bulk Reef Supply"
              {...form.getInputProps('vendor')}
            />
          </Stack>
        </Fieldset>

        <Textarea
          label="Notes"
          placeholder="Additional notes about this equipment..."
          minRows={3}
          {...form.getInputProps('notes')}
        />

        <Group justify={isEditMode && onDelete ? 'space-between' : 'flex-end'} mt="md">
          {isEditMode && onDelete && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onDelete(equipment!.id)}
            >
              Delete
            </Button>
          )}
          <Group gap="sm">
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} disabled={isLoading}>
              {isEditMode ? 'Update Equipment' : 'Add Equipment'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
}
